import { createSelector, createSlice } from "@reduxjs/toolkit";
import { isEqual } from "lodash";
import { useSelector } from "react-redux";
import { insAssetsStateId, insFundsStateId, insInvestorsStateId, insManagersStateId, insPortfoliosStateId, insPacingPlansStateId } from "common/constants";
import { ASSETS_PAGE, FUNDS_PAGE, MANAGERS_PAGE, PORTFOLIOS_PAGE, COMMITMENT_PACING_PLANS_PAGE } from "common/constants/routes";
import { isLocationChangeAction } from "store/current-dashboard/utils";
const initialState = {
    filter: {},
    state: {
        "all-InvestorAccount-grid": { currency: "USD", hideInitialPlaceholder: true },
        "all-funds-grid": { currency: "USD" },
        "manager-grid": { currency: "USD" },
        "portfolio-creation-add-investor-account-grid": { hideInitialPlaceholder: true },
        "portfolio-creation-manual-composition-grid": { currency: "USD" },
        "portfolio-creation-rules-based-grid": { hideInitialPlaceholder: true }
    },
    temporaryKey: "",
    temporaryState: []
};
const isPortfoliosLocationChangeAction = (action) => isLocationChangeAction(action) &&
    action.payload?.location?.pathname?.startsWith(PORTFOLIOS_PAGE);
const isFundsLocationChangeAction = (action) => isLocationChangeAction(action) &&
    action.payload?.location?.pathname?.startsWith(FUNDS_PAGE);
const isAssetsLocationChangeAction = (action) => isLocationChangeAction(action) &&
    action.payload?.location?.pathname?.startsWith(ASSETS_PAGE);
const isManagersLocationChangeAction = (action) => isLocationChangeAction(action) &&
    action.payload?.location?.pathname?.startsWith(MANAGERS_PAGE);
const isPacingPlansLocationChangeAction = (action) => isLocationChangeAction(action) &&
    action.payload?.location?.pathname?.startsWith(COMMITMENT_PACING_PLANS_PAGE);
const createIfNotFound = (state, id, partialInit) => {
    state.state[id] = {
        ...partialInit || {},
        ...state.state[id] || {}
    };
};
const slice = createSlice({
    extraReducers: (builder) => builder
        .addMatcher(isPortfoliosLocationChangeAction, (state) => {
        if (state.state[insPortfoliosStateId]?.selectedRows) {
            state.state[insPortfoliosStateId].selectedRows = [];
        }
    })
        .addMatcher(isFundsLocationChangeAction, (state) => {
        if (state.state[insFundsStateId]?.selectedRows) {
            state.state[insFundsStateId].selectedRows = [];
        }
        if (state.state[insInvestorsStateId]?.selectedRows) {
            state.state[insInvestorsStateId].selectedRows = [];
        }
    })
        .addMatcher(isAssetsLocationChangeAction, (state) => {
        if (state.state[insAssetsStateId]?.selectedRows) {
            state.state[insAssetsStateId].selectedRows = [];
        }
    })
        .addMatcher(isManagersLocationChangeAction, (state) => {
        if (state.state[insManagersStateId]?.selectedRows) {
            state.state[insManagersStateId].selectedRows = [];
        }
    })
        .addMatcher(isPacingPlansLocationChangeAction, (state) => {
        if (state.state[insPacingPlansStateId]?.selectedRows) {
            state.state[insPacingPlansStateId].selectedRows = [];
        }
    }),
    initialState,
    name: "gridsReducers",
    reducers: {
        addSelected: (state, action) => {
            createIfNotFound(state, action.payload.id, { selectedRows: [] });
            state.state[action.payload.id].selectedRows?.push(action.payload.row || "");
        },
        addSelectedRows: (state, action) => {
            createIfNotFound(state, action.payload.id);
            state.state[action.payload.id].selectedRows = [...new Set([...state.state[action.payload.id].selectedRows || [], ...action.payload.selectedRows])];
        },
        clearSelectedRows: (state, action) => {
            if (state.state[action.payload]?.selectedRows) {
                delete state.state[action.payload].selectedRows;
            }
        },
        removeAllEditedRow: (state, action) => {
            createIfNotFound(state, action.payload);
            state.state[action.payload].editedRows = {};
        },
        removeAllSelectedRow: (state, action) => {
            createIfNotFound(state, action.payload);
            state.state[action.payload].selectedRows = [];
        },
        removeEditedRow: (state, action) => {
            createIfNotFound(state, action.payload.id);
            if (action.payload.row && state.state[action.payload.id].editedRows?.[action.payload.row]) {
                delete state.state[action.payload.id].editedRows?.[action.payload.row];
            }
        },
        removeSelectedRow: (state, action) => {
            createIfNotFound(state, action.payload.id);
            const index = state.state[action.payload.id].selectedRows?.indexOf(action.payload.row || "") ?? -1;
            if (index !== -1) {
                state.state[action.payload.id].selectedRows?.splice(index, 1);
            }
        },
        resetFundAndInvestorSelectedRows: (state) => {
            state.state[insFundsStateId].selectedRows = [];
            state.state[insInvestorsStateId].selectedRows = [];
        },
        resetTemporaryState: (state) => {
            state.temporaryState = initialState.temporaryState;
        },
        saveState: (state, action) => {
            state.state[action.payload.id] = {
                ...state.state[action.payload.id],
                columns: action.payload.columns ?? [],
                selectedRows: state.state[action.payload.id]?.selectedRows ? state.state[action.payload.id]?.selectedRows : []
            };
        },
        setCurrency: (state, action) => {
            state.state[action.payload.id] = { ...state.state[action.payload.id], currency: action.payload.currency };
        },
        setEditedRow: (state, action) => {
            createIfNotFound(state, action.payload.id, { editedRows: {} });
            const edited = state.state[action.payload.id].editedRows;
            edited[action.payload.key] = action.payload.value;
        },
        setEditedRows: (state, action) => {
            createIfNotFound(state, action.payload.id, { editedRows: {} });
            Object.keys(action.payload.editedRows).forEach((key) => {
                if (!state.state[action.payload.id].editedRows?.[key]) {
                    state.state[action.payload.id].editedRows[key] = action.payload.editedRows[key];
                }
            });
        },
        setFilter: (state, action) => {
            state.filter[action.payload.id] = { ...action.payload.filterModel };
        },
        setInvestorView: (state, action) => {
            state.state[action.payload.id] = { ...state.state[action.payload.id], investorView: action.payload.enabled };
        },
        setIsStriped: (state, action) => {
            state.state[action.payload.id] = { ...state.state[action.payload.id], isStriped: action.payload.isStriped };
        },
        setSearch: (state, action) => {
            state.state[action.payload.id] = { ...state.state[action.payload.id], search: action.payload.search };
        },
        setSelectedRowsState: (state, action) => {
            state.state[action.payload.id] = { ...state.state[action.payload.id], selectedRows: action.payload.selectedRows };
        },
        setTemporaryKey: (state, action) => {
            state.temporaryKey = action.payload;
        },
        setTemporaryState: (state, action) => {
            state.temporaryState = action.payload;
        }
    }
});
const getDefaultGroupedColumns = (cols) => {
    const colsGroupedByDefault = cols?.filter((e) => e.rowGroup);
    return colsGroupedByDefault?.map((e) => e.colId || e.field) || [];
};
const isColumnGrouped = (column, cols) => {
    if (column.rowGroup) {
        return true;
    }
    if (column.rowGroup === false) {
        return false;
    }
    return cols?.find((e) => e.colId === column.colId)?.rowGroup || false;
};
const ensureRowGroupIndex = (column, cols) => {
    if (column.rowGroupIndex !== undefined) {
        return column;
    }
    const defaultCol = cols?.find((e) => (e.colId === column.colId || e.field === column.colId) && e.rowGroup);
    return defaultCol ? { ...column, rowGroupIndex: 0 } : column;
};
const getSortIndex = (column) => (column.rowGroupIndex === undefined ? Number.MAX_SAFE_INTEGER : column.rowGroupIndex);
export const useGetGridGroups = (stateId, cols) => {
    const columnState = useSelector((state) => state.grids.state[stateId]?.columns, isEqual);
    if (!columnState?.length) {
        return getDefaultGroupedColumns(cols);
    }
    const groupedColumns = columnState
        .filter((c) => isColumnGrouped(c, cols))
        .map((c) => ensureRowGroupIndex(c, cols));
    const sortedColumns = [...groupedColumns].sort((a, b) => getSortIndex(a) - getSortIndex(b));
    return sortedColumns.map((c) => c.colId || c.id || "");
};
export const useHasGridColumnState = (stateId) => {
    const columnState = useSelector((state) => state.grids.state[stateId]?.columns, isEqual);
    return columnState !== undefined && columnState !== null && columnState.length > 0;
};
export const gridCurrencySelector = createSelector((state) => state.grids.state, (_, id) => id, (state, id) => state[id]?.currency || "");
export const gridInvestorViewSelector = createSelector((state) => state.grids.state, (_, id) => id, (state, id) => state[id]?.investorView || false);
export const gridInitialOptionPlaceholderSelector = createSelector((state) => state.grids.state, (_, id) => id, (state, id) => state[id]?.hideInitialPlaceholder ?? false);
export const gridsReducers = slice.reducer;
export const { setIsStriped, resetTemporaryState, saveState, setSelectedRowsState, setSearch, removeAllSelectedRow, removeSelectedRow, removeAllEditedRow, removeEditedRow, addSelected, addSelectedRows, setTemporaryState, clearSelectedRows, setFilter, setCurrency, setInvestorView, setEditedRow, setEditedRows, resetFundAndInvestorSelectedRows, setTemporaryKey } = slice.actions;
